import React from "react"
import { useDispatch, useSelector } from "react-redux";
import { StyledModalBody } from "../../../../components/Modals";
import { ButtonBodyVariant } from "../../../../components/ui/ButtonVariant";
import { useTranslation } from "react-i18next";
import { closeActiveModal, openModal } from "../../../../helper/modals";
import { Flex } from "../../../../components/ui/Flex";
import { Form, FormControl, FormField, FormItem, FormMessage } from "../../../../components/ui/Form";
import { useForm } from "react-hook-form";
import { DescriptionText, ModalButtonsContainer, PaymentModal, TitleText } from "../../PaymentComponents";
import * as Yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";
import { updatePaymentData } from "../../../../redux/actions/payments";
import { ToggleGroup, useToggleGroupContext } from "../../../../components/ui/ToggleGroup";
import styled from "styled-components";
import { IoCheckmarkCircle } from "react-icons/io5";
import { MdOutlineCircle } from "react-icons/md";
import { Palette } from "../../../../components/utils";

const PAYMENT_OPTIONS = [
  {
    title: "Recurring",
    description: "Set up automatic payments that repeat every specified interval.",
    nextModal: 'CreateRecurringPaymentStep1',
  },
  {
    title: "One Time Payment",
    description: "Charge a single payment for items like move-in fees or one-time services.",
    nextModal: 'CreateOneTimePaymentStep1',
  }
]

const schema = Yup.object().shape({
  paymentOption: Yup.mixed()
    .required('A payment selection is required')
});

const SelectPaymentTypeModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const modal = useSelector((state) => state.headerModal.modal);
  const addPayment = useSelector((state) => state.addPayment);

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      paymentOption: addPayment.paymentOption
    }
  });

  const getPaymentOptionFromTitle = (title) => PAYMENT_OPTIONS.find((value) => value.title === title)

  const onSubmit = (values) => {
    dispatch(updatePaymentData(values))
    const nextPaymentOptionModal = getPaymentOptionFromTitle(values.paymentOption[0]).nextModal
    openModal(nextPaymentOptionModal)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <PaymentModal
          show={modal === 'SelectPaymentTypeModal'}
          onClose={closeActiveModal}
          closeOnBackdrop={false}
          width={'460px'}>
          <StyledModalBody>
            <Flex direction="column" gap="1rem">
              <TitleText>Select Payment Type</TitleText>

              <Flex direction="column" alignItems="center" gap="1rem">
                <FormField
                  control={form.control}
                  name="paymentOption"
                  render={({ field }) => (
                    <FormItem alt>
                      <FormControl>
                        <ToggleGroup
                          single
                          {...field}
                          onValueChange={(values) => {
                            field.onChange(values);
                          }}>

                          {PAYMENT_OPTIONS.map((paymentOption) => {
                            return (
                              <ToggleGroupItem value={paymentOption.title} key={paymentOption.title} paymentOption={paymentOption} />
                            )
                          })}

                        </ToggleGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              </Flex>
              <ModalButtonsContainer gapY="8px">
                <ButtonBodyVariant cssOverride="width: 8.875rem; height: 2rem;" type="button" variant="secondary" onClick={closeActiveModal}>{t('Cancel')}</ButtonBodyVariant>
                <ButtonBodyVariant cssOverride="width: 8.875rem; height: 2rem;" type="submit" disabled={!form.formState.isValid}>{t('Next')}</ButtonBodyVariant>
              </ModalButtonsContainer>
            </Flex>
          </StyledModalBody>
        </PaymentModal>
      </form>
    </Form>
  )
}

const ToggleGroupItem = ({ value, paymentOption, ...rest }) => {
  const toggleGroupContext = useToggleGroupContext();
  console.log('toggleGroupContext', toggleGroupContext)
  const selected = toggleGroupContext.values.includes(value);

  return (
    <PaymentOptionContainer
      selected={selected}
      onClick={() => {
        if (selected) {
          toggleGroupContext.onItemDeactivate(value);
        } else {
          toggleGroupContext.onItemActivate(value);
        }
      }}
      {...rest}>

      {selected ? <IoCheckmarkCircle size={22} style={{ flexShrink: 0 }} /> : <MdOutlineCircle size={22} style={{ flexShrink: 0 }} />}

      <Flex direction="column" justify="start" alignItems="start">
        <TitleText>{paymentOption.title}</TitleText>
        <DescriptionText style={{ textAlign: "left" }}>{paymentOption.description}</DescriptionText>
      </Flex>
    </PaymentOptionContainer>
  );
};

const PaymentOptionContainer = styled(Flex)`
  min-height: 8.5rem;
  padding: 1.5rem 1.125rem;
  align-items: center;
  gap: 1.5rem;

  border-radius: 0.625rem;
  border: 1px solid ${Palette.DISABLED};
  background: ${Palette.WHITE};

  cursor: pointer;

  ${({ selected }) =>
    selected &&
    `
    border: 1px solid ${Palette.HAZY_BLUE};
    background: ${Palette.SOLITUDE};
  `}
`

export default SelectPaymentTypeModal